import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Search, User, Building2, X, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CustomerPicker - Searchable dropdown to select from customer database
 * @param {function} onSelect - Callback when customer is selected: (customer) => void
 * @param {string} selectedCustomerId - Currently selected customer ID (if any)
 * @param {function} onClear - Callback to clear selection
 */
export default function CustomerPicker({ onSelect, selectedCustomerId, onClear }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Load selected customer if ID provided
  useEffect(() => {
    if (selectedCustomerId && !selectedCustomer) {
      loadCustomerById(selectedCustomerId);
    }
  }, [selectedCustomerId]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Search customers when search changes
  useEffect(() => {
    if (isOpen && search.length > 0) {
      searchCustomers(search);
    }
  }, [search, isOpen]);

  const loadCustomerById = async (id) => {
    try {
      const res = await axios.get(`${API}/customers/${id}`);
      setSelectedCustomer(res.data);
    } catch (error) {
      console.error("Failed to load customer", error);
    }
  };

  const searchCustomers = async (query) => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/customers`, {
        params: { search: query, limit: 10 }
      });
      setCustomers(res.data.customers || []);
    } catch (error) {
      console.error("Failed to search customers", error);
      setCustomers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadRecentCustomers = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/customers`, {
        params: { limit: 5 }
      });
      setCustomers(res.data.customers || []);
    } catch (error) {
      console.error("Failed to load recent customers", error);
      setCustomers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    setSearch("");
    loadRecentCustomers();
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleSelect = (customer) => {
    setSelectedCustomer(customer);
    setIsOpen(false);
    onSelect(customer);
  };

  const handleClear = () => {
    setSelectedCustomer(null);
    onClear && onClear();
  };

  if (selectedCustomer) {
    return (
      <div className="flex items-center gap-2 p-2 bg-aa-bg rounded-aa-md border border-gray-200">
        <div className="w-8 h-8 bg-aa-primary/10 rounded-full flex items-center justify-center">
          {selectedCustomer.business_name ? (
            <Building2 className="w-4 h-4 text-aa-primary" />
          ) : (
            <User className="w-4 h-4 text-aa-primary" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm text-gray-900 truncate">
            {selectedCustomer.business_name || selectedCustomer.contact_name}
          </div>
          {selectedCustomer.business_name && selectedCustomer.contact_name && (
            <div className="text-xs text-gray-500 truncate">
              {selectedCustomer.contact_name}
            </div>
          )}
        </div>
        <Badge variant="outline" className="text-xs shrink-0">
          {selectedCustomer.default_client_type}
        </Badge>
        <button
          type="button"
          onClick={handleClear}
          className="p-1 text-gray-400 hover:text-gray-600"
          title="Clear selection"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        type="button"
        variant="outline"
        onClick={handleOpen}
        className="w-full justify-start text-gray-500 font-normal"
        data-testid="customer-picker-btn"
      >
        <User className="w-4 h-4 mr-2" />
        Select existing customer...
      </Button>

      {isOpen && (
        <div className="absolute z-50 top-full mt-1 w-full bg-white rounded-aa-md shadow-lg border border-gray-200 max-h-80 overflow-hidden">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                ref={inputRef}
                type="text"
                placeholder="Search customers..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9"
                data-testid="customer-search-input"
              />
            </div>
          </div>
          
          <div className="max-h-64 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center text-sm text-gray-500">
                Searching...
              </div>
            ) : customers.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                {search ? "No customers found" : "Start typing to search"}
              </div>
            ) : (
              <div className="py-1">
                {customers.map((customer) => (
                  <button
                    key={customer.id}
                    type="button"
                    onClick={() => handleSelect(customer)}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-aa-bg text-left transition-colors"
                    data-testid={`customer-option-${customer.id}`}
                  >
                    <div className="w-8 h-8 bg-aa-primary/10 rounded-full flex items-center justify-center shrink-0">
                      {customer.business_name ? (
                        <Building2 className="w-4 h-4 text-aa-primary" />
                      ) : (
                        <User className="w-4 h-4 text-aa-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-900 truncate">
                        {customer.business_name || customer.contact_name}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {customer.email || customer.phone || "No contact info"}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs shrink-0">
                      {customer.default_client_type}
                    </Badge>
                    {customer.id === selectedCustomerId && (
                      <Check className="w-4 h-4 text-aa-primary shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
